import React, { useRef } from 'react';
import { AnchoredPortal } from './AnchoredPortal';

export interface PopoverButtonProps {
  label: string;
  icon?: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Shown as a small badge next to the label when > 0. */
  activeCount?: number;
  children: React.ReactNode;
  estimatedWidth?: number;
  estimatedHeight?: number;
  className?: string;
  'data-testid'?: string;
}

/**
 * Generic trigger button + AnchoredPortal panel shell, shared by FilterButton
 * and any other compact popover control (e.g. Slack's view/grouping button).
 * Owns no filtering logic — callers render whatever they need in `children`.
 */
export const PopoverButton: React.FC<PopoverButtonProps> = ({
  label,
  icon,
  open,
  onOpenChange,
  activeCount = 0,
  children,
  estimatedWidth,
  estimatedHeight,
  className,
  'data-testid': testId,
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid={testId}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '7px 12px',
          borderRadius: 999,
          fontSize: 12.5,
          fontWeight: 500,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          background: 'var(--z-surface)',
          color: 'var(--z-muted)',
          border: 'none',
        }}
      >
        {icon}
        <span>{label}</span>
        {activeCount > 0 && (
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10.5,
              fontWeight: 600,
              padding: '1px 6px',
              borderRadius: 6,
              background: 'var(--z-accent)',
              color: 'var(--z-on-accent)',
            }}
          >
            {activeCount}
          </span>
        )}
      </button>
      <AnchoredPortal
        open={open}
        anchorRef={anchorRef}
        onClose={() => onOpenChange(false)}
        className={className}
        role="dialog"
        aria-label={label}
        estimatedWidth={estimatedWidth}
        estimatedHeight={estimatedHeight}
      >
        <div
          style={{
            background: 'var(--z-surface)',
            border: '1px solid var(--z-border)',
            borderRadius: 12,
            boxShadow: '0 10px 28px rgba(0, 0, 0, 0.12)',
          }}
        >
          {children}
        </div>
      </AnchoredPortal>
    </div>
  );
};
